/**
 * Builds the document for previewing pasted or generated widget code inside an
 * iframe with sandbox="allow-scripts" (no allow-same-origin), so the code can't
 * reach the admin page or its session.
 *
 * In such a frame localStorage, sessionStorage and document.cookie throw a
 * SecurityError. Widget code often uses them (for example to remember a
 * carousel position), and on the live page they work, so the preview gives
 * the code in-memory versions instead. They reset on every preview render.
 */
import { scopeCss } from 'shared/lib/widgetCode/scopeCss';
import { scopeCssLegacyAi } from 'shared/lib/widgetCode/legacyScopers';
import { wrapWidgetScriptV2 } from 'shared/lib/widgetScriptRuntime';
import { wrapScript as wrapAiScript } from 'shared/widgets/AiGeneratedWidget/versions/0.0.1/AiGeneratedWidget';
import { sanitizeHtml } from 'shared/components/CleanedHtml';
import { PREVIEW_STORAGE_SHIM } from 'shared/lib/previewStorageShim';

export { PREVIEW_STORAGE_SHIM };

// A literal "</script" or "</style" inside the code would end the element early.
// "<\/" means the same thing inside JS strings, comments and regexes, and inside
// CSS strings and comments, where these sequences can appear.
export const escapeClosingTag = (code: string, tag: 'script' | 'style'): string =>
  code.replace(new RegExp(`</(${tag})`, 'gi'), '<\\/$1');

// The page fonts ff.com uses, so the preview looks like the live section.
const SITE_BASE = [
  '<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@300;400;500;700&family=Roboto:wght@300;400;500;700&display=swap">',
  '<style>body{margin:0;font-family:Roboto,"Noto Sans SC",sans-serif;-webkit-font-smoothing:antialiased}</style>'
].join('');

export type PreviewInput = {
  html?: string;
  css?: string;
  js?: string;
  scopeClass?: string;
  /** 2 for code saved with the v2 tools; anything else uses the original AI widget scoper. */
  scoper?: number;
  /** False for proposals: nothing in the pasted code runs until it is kept. */
  runScripts?: boolean;
};

/**
 * The preview renders the section the way the live AI widget does: inside an
 * element with its scope class, with the same sanitized HTML, scoped CSS and
 * script wrapper.
 */
export const buildPreviewDocument = ({ html, css, js, scopeClass, scoper, runScripts = true }: PreviewInput): string => {
  const scope = (scopeClass || 'ai-widget-preview').replace(/[^\w-]/g, '-');
  const isV2 = scoper === 2;
  const scopedCss = isV2 ? scopeCss(css || '', scope).css : scopeCssLegacyAi(css || '', scope);
  const script = !runScripts || !(js || '').trim() ? '' : isV2
    ? wrapWidgetScriptV2({ js: js || '', rootSelector: `.${scope}`, label: 'AI widget' })
    : wrapAiScript(js || '', scope);
  return [
    '<!doctype html><html><head><meta charset="utf-8">',
    runScripts ? `<script>${PREVIEW_STORAGE_SHIM}</script>` : '',
    SITE_BASE,
    `<style>${escapeClosingTag(scopedCss, 'style')}</style>`,
    '</head><body>',
    // Sanitized like the live widget, so e.g. inline onclick is gone here too. The inner <div> is the
    // live page's too (CleanedHtml), so selectors like `.ai-widget-root > section` match the same.
    `<div class="ai-widget-root ${scope}"><div>${sanitizeHtml(html || '')}</div></div>`,
    script ? `<script>${escapeClosingTag(script, 'script')}</script>` : '',
    '</body></html>'
  ].join('');
};
